/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import type { ReactElement, RefObject } from 'react';

import { Button, ChipGroup, Grid } from '@coursera/cds-core';
import { AddIcon } from '@coursera/cds-icons';

import OptionPill from 'bundles/onboarding-common/components/OptionPill';
import type { SelectOptionWithState } from 'bundles/onboarding-common/types';

import _t from 'i18n!nls/onboarding-common';

type BaseItem = {
  id: string;
};

type BaseSelectQuestionProps<T extends BaseItem> = {
  itemsPerRow: SelectOptionWithState<T>[][] | null;
  containerRef: RefObject<HTMLDivElement>;
  handleClick: (item: SelectOptionWithState<T>) => void;
  onViewMoreClick: () => void;
  onViewAllClick: () => void;
  isLoading: boolean;
  isExpanded: boolean;
  getLabel: (item: SelectOptionWithState<T>) => string;
  getIcon?: (item: SelectOptionWithState<T>) => ReactElement | undefined;
  getLabelSpacing?: (item: SelectOptionWithState<T>) => 'small' | 'medium';
  ariaLabel: string;
  shouldUseDefaultLayout: boolean;
};

const BaseSelectQuestion = <T extends BaseItem>({
  itemsPerRow,
  containerRef,
  handleClick,
  onViewMoreClick,
  onViewAllClick,
  isLoading,
  isExpanded,
  getLabel,
  getIcon,
  getLabelSpacing,
  ariaLabel,
  shouldUseDefaultLayout,
}: BaseSelectQuestionProps<T>) => {
  if (!itemsPerRow) {
    return null;
  }

  const styles = {
    itemsWrapper: (shouldWrap: boolean) => css`
      display: flex;
      flex-wrap: ${shouldWrap ? 'wrap' : 'nowrap'};
      overflow: hidden;
      gap: var(--cds-spacing-100);
    `,
  };

  const renderItem = (item: SelectOptionWithState<T>) => {
    const icon = getIcon?.(item);
    if (icon) {
      return (
        <OptionPill
          key={item.id}
          label={getLabel(item)}
          icon={icon}
          onClick={() => handleClick(item)}
          selected={item.isSelected}
          variant="withImageIcon"
        />
      );
    }
    return (
      <OptionPill
        key={item.id}
        label={getLabel(item)}
        onClick={() => handleClick(item)}
        selected={item.isSelected}
        variant="textOnly"
        spacing={getLabelSpacing?.(item) || 'medium'}
      />
    );
  };

  const renderButton = () => {
    return (
      <Button
        variant="ghost"
        size="small"
        icon={<AddIcon size="medium" />}
        iconPosition="before"
        onClick={isExpanded ? onViewAllClick : onViewMoreClick}
      >
        {isExpanded ? _t('View all') : _t('View more')}
      </Button>
    );
  };

  const renderCustomLayout = () => {
    return itemsPerRow.map((itemsRow, index) => (
      <div key={itemsRow[0]?.id} css={styles.itemsWrapper(false)}>
        {itemsRow.map((item) => renderItem(item))}
        {index === itemsPerRow.length - 1 && renderButton()}
      </div>
    ));
  };

  const renderDefaultLayout = () => {
    return (
      <div css={styles.itemsWrapper(true)}>
        {itemsPerRow.flat().map((item) => renderItem(item))}
        {renderButton()}
      </div>
    );
  };

  return (
    <Grid item xs={12} ref={containerRef}>
      {isLoading ? null : (
        <ChipGroup multiSelect={true} aria-label={ariaLabel}>
          {shouldUseDefaultLayout ? renderDefaultLayout() : renderCustomLayout()}
        </ChipGroup>
      )}
    </Grid>
  );
};

export default BaseSelectQuestion;
